import React, {useEffect, useMemo, useState} from 'react';
import {CButton, CInput, CSelect} from '@coreui/react';
import CIcon from "@coreui/icons-react";
import Select from "react-select";
import {getApi} from "../../apis/backendApis";
import {Fluxo, Papel, Referencia, TipoPapel, TipoPapelList} from "./modelo";

interface Props {
    fluxo: Fluxo,
    somenteLeitura: boolean,
    onMudar: (papeis: Papel[], renomeado?: {de: string, para: string}) => void,
    /** Setores do fluxo; os papeis vao junto, sem o setor e a pessoa que ficaram de fora. */
    onMudarSetores: (setores: Referencia[], papeis: Papel[]) => void,
}

interface Setor extends Referencia {
    usuarios: Referencia[],
}

/**
 * Papeis do fluxo: quem executa etapas e itens. O setor e o usuario padrao so
 * pre-preenchem a abertura do processo, onde o gestor escolhe as pessoas.
 *
 * Setores do fluxo (#484909): com algum, so os usuarios deles ocupam os
 * papeis, aqui (pessoa padrao), na abertura e na troca de pessoa do processo.
 */
const PapeisTab = ({fluxo, somenteLeitura, onMudar, onMudarSetores}: Props) => {
    const [setores, setSetores] = useState<Setor[]>([]);
    const [usuarios, setUsuarios] = useState<Referencia[]>([]);
    const ro = somenteLeitura;

    useEffect(() => {
        const api = getApi();
        // Os ids vem como texto da API (Postgres no PHP 7): normaliza para comparar com os do fluxo.
        api.get('/setor/suggest').then(resposta => setSetores((resposta.data.setores || []).map((s: any) => ({
            id: Number(s.id), nome: String(s.nome), usuarios: (s.usuarios || []).map((u: any) => ({id: Number(u.id), nome: String(u.nome)})),
        }))));
        api.get('/usuarios/suggest').then(resposta => {
            // Formato da API: [{"12": "Nome"}, ...]
            const lista = (resposta.data.usuarios || []).map((par: any) => {
                const [id, nome] = Object.entries(par)[0];
                return {id: Number(id), nome: String(nome)};
            });
            setUsuarios(lista);
        });
    }, []);

    const uso = useMemo(() => {
        const contagem: {[codigo: string]: number} = {};
        fluxo.etapas.forEach(etapa => {
            contagem[etapa.papel] = (contagem[etapa.papel] || 0) + 1;
            etapa.itens.forEach(item => {
                contagem[item.papel] = (contagem[item.papel] || 0) + 1;
            });
        });
        return contagem;
    }, [fluxo.etapas]);

    const muda = (indice: number, campo: keyof Papel, valor: any) => {
        const antigo = fluxo.papeis[indice];
        const papeis = fluxo.papeis.map((p, i) => (i === indice ? {...p, [campo]: valor} : p));
        const codigoRepetido = campo === 'codigo' && fluxo.papeis.some((p, i) => i !== indice && p.codigo === valor);
        onMudar(papeis, campo === 'codigo' && !codigoRepetido ? {de: antigo.codigo, para: valor} : undefined);
    };

    const mudaSetor = (indice: number, idSetor: string) => {
        const setor = setores.find(s => String(s.id) === idSetor) || null;
        const papel = fluxo.papeis[indice];
        // Usuario padrao fora do setor escolhido nao faz sentido.
        const usuario = setor && papel.usuario && !setor.usuarios.some(u => u.id === papel.usuario!.id) ? null : papel.usuario;
        onMudar(fluxo.papeis.map((p, i) => (i === indice ? {...p, setor: setor ? {id: setor.id, nome: setor.nome} : null, usuario} : p)));
    };

    const mudaUsuario = (indice: number, idUsuario: string) => {
        const usuario = usuarios.find(u => String(u.id) === idUsuario) || null;
        muda(indice, 'usuario', usuario);
    };

    const adiciona = () => {
        onMudar([...fluxo.papeis, {codigo: '', nome: '', tipo: TipoPapel.INTERNO, setor: null, usuario: null}]);
    };

    const remove = (indice: number) => onMudar(fluxo.papeis.filter((_, i) => i !== indice));

    const move = (indice: number, direcao: -1 | 1) => {
        const papeis = [...fluxo.papeis];
        const [p] = papeis.splice(indice, 1);
        papeis.splice(indice + direcao, 0, p);
        onMudar(papeis);
    };

    const doFluxo = fluxo.setores || [];
    // Quem pode ocupar papel: os usuarios dos setores do fluxo (sem setor, todos).
    const permitidos = useMemo(() => {
        if (!doFluxo.length) return usuarios;
        const mapa = new Map<number, Referencia>();
        setores.filter(s => doFluxo.some(d => d.id === s.id)).forEach(s => s.usuarios.forEach(u => mapa.set(u.id, u)));
        return Array.from(mapa.values()).sort((a, b) => a.nome.localeCompare(b.nome));
    }, [doFluxo, setores, usuarios]);
    const setoresSugeridos = doFluxo.length ? setores.filter(s => doFluxo.some(d => d.id === s.id)) : setores;

    const usuariosDoPapel = (papel: Papel) => {
        const setor = papel.setor && setores.find(s => s.id === papel.setor!.id);
        return setor ? setor.usuarios : permitidos;
    };

    /** Troca os setores do fluxo e tira dos papeis o setor e a pessoa que ficaram de fora. */
    const mudaSetores = (escolhidos: Referencia[]) => {
        const ids = escolhidos.map(s => s.id);
        const pessoas = new Set<number>();
        setores.filter(s => ids.includes(s.id)).forEach(s => s.usuarios.forEach(u => pessoas.add(u.id)));
        const papeis = fluxo.papeis.map(p => ({
            ...p,
            setor: ids.length && p.setor && !ids.includes(p.setor.id) ? null : p.setor,
            usuario: ids.length && p.usuario && !pessoas.has(p.usuario.id) ? null : p.usuario,
        }));
        onMudarSetores(escolhidos, papeis);
    };

    return (
        <div className="fx-aba">
            <div className="fx-aba-conteudo">
                <section className="fx-setores">
                    <label htmlFor="fx-setores-select">
                        <strong>Setores do fluxo</strong>
                        <span className="text-muted small">
                            {!doFluxo.length
                                ? ' · sem setor, qualquer usuário ativo ocupa os papéis'
                                : !setores.length
                                    ? ' · carregando…'
                                    : ` · só os ${permitidos.length} usuários desses setores ocupam os papéis (aqui, na abertura do processo e ao trocar a pessoa de um papel)`}
                        </span>
                    </label>
                    <Select inputId="fx-setores-select" isMulti isDisabled={ro} classNamePrefix="fx-select" placeholder="Todos os setores"
                            options={setores.map(s => ({value: s.id, label: `${s.nome} (${s.usuarios.length})`}))}
                            value={doFluxo.map(s => ({value: s.id, label: s.nome}))}
                            noOptionsMessage={() => 'Nenhum setor com usuários ativos'}
                            onChange={(valor: any) => mudaSetores((valor || []).map((v: any) => ({id: v.value, nome: String(v.label).replace(/ \(\d+\)$/, '')})))}/>
                </section>
                <p className="text-muted small mb-3">
                    Papel é quem executa uma etapa ou item. Na abertura do processo, cada papel recebe uma pessoa:
                    um usuário interno ou, nos papéis do tipo Cliente, um contato do cliente. Setor e usuário aqui são só a sugestão inicial.
                </p>
                <div className="fx-tabela">
                    <div className="fx-tabela-cabecalho papeis">
                        <span>Código</span><span>Nome</span><span>Tipo</span><span>Setor sugerido</span><span>Usuário padrão</span><span>Uso</span><span/>
                    </div>
                    {fluxo.papeis.map((papel, i) => (
                        <div className="fx-tabela-linha papeis" key={papel.id ?? `novo-${i}`}>
                            <CInput size="sm" value={papel.codigo} maxLength={20} disabled={ro} placeholder="FIS"
                                    onChange={(e: any) => muda(i, 'codigo', e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}/>
                            <CInput size="sm" value={papel.nome} maxLength={120} disabled={ro} placeholder="Responsável Fiscal"
                                    onChange={(e: any) => muda(i, 'nome', e.target.value)}/>
                            <CSelect size="sm" value={papel.tipo} disabled={ro} onChange={(e: any) => muda(i, 'tipo', Number(e.target.value))}>
                                {TipoPapelList.getList().map((t: any) => <option key={t.value} value={t.value}>{t.description}</option>)}
                            </CSelect>
                            <CSelect size="sm" value={papel.setor?.id ?? ''} disabled={ro || papel.tipo === TipoPapel.CLIENTE}
                                     onChange={(e: any) => mudaSetor(i, e.target.value)}>
                                <option value="">—</option>
                                {papel.setor && !setores.some(s => s.id === papel.setor!.id) && <option value={papel.setor.id}>{papel.setor.nome}</option>}
                                {setoresSugeridos.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                            </CSelect>
                            <CSelect size="sm" value={papel.usuario?.id ?? ''} disabled={ro || papel.tipo === TipoPapel.CLIENTE}
                                     onChange={(e: any) => mudaUsuario(i, e.target.value)}>
                                <option value="">—</option>
                                {papel.usuario && !usuariosDoPapel(papel).some(u => u.id === papel.usuario!.id) && <option value={papel.usuario.id}>{papel.usuario.nome}</option>}
                                {usuariosDoPapel(papel).map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                            </CSelect>
                            <span className="fx-uso" title="Etapas e itens que usam o papel">{uso[papel.codigo] || 0}</span>
                            <span className="fx-botoes">
                                {!ro && (
                                    <>
                                        <CButton size="sm" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} title="Subir"><CIcon name="cil-arrow-top"/></CButton>
                                        <CButton size="sm" variant="ghost" disabled={i === fluxo.papeis.length - 1} onClick={() => move(i, 1)} title="Descer"><CIcon name="cil-arrow-bottom"/></CButton>
                                        <CButton size="sm" variant="ghost" color="danger" disabled={!!uso[papel.codigo]} onClick={() => remove(i)}
                                                 title={uso[papel.codigo] ? 'Em uso: troque o papel das etapas e itens antes' : 'Excluir'}>
                                            <CIcon name="cil-trash"/>
                                        </CButton>
                                    </>
                                )}
                            </span>
                        </div>
                    ))}
                </div>
                {!ro && (
                    <CButton size="sm" color="success" variant="outline" className="mt-2" onClick={adiciona}>
                        <CIcon name="cil-plus"/> Papel
                    </CButton>
                )}
            </div>
        </div>
    );
};

export default PapeisTab;
